import React, { HTMLInputTypeAttribute, Ref, useId } from "react"
import { HiX } from "react-icons/hi"
import { TbExclamationCircleFilled } from "react-icons/tb"
import { AutocompleteToken, FormFieldProps, FormFieldTheme } from "src/components/admin/form/types"
import { ICON_BUTTON_LABELS } from "src/utils/a11y.util"
import { twMerge } from "tailwind-merge"

type InputFieldProps = FormFieldProps & {
   acceptChars?: RegExp
   ariaLabel?: string
   autoFocus?: boolean
   clearable?: boolean
   description?: string
   disabled?: boolean
   successIcon?: React.ReactNode | React.ReactElement
   endIcon?: React.ReactNode | React.ReactElement
   error?: string
   maxChars?: number
   name?: string
   placeholder?: string
   ref?: Ref<HTMLInputElement>
   startIcon?: React.ReactNode | React.ReactElement
   startIconLabel?: string
   endIconLabel?: string
   theme?: FormFieldTheme
   title?: React.ReactNode
   type?: HTMLInputTypeAttribute
   value?: string
   autoComplete?: AutocompleteToken
   onStartIconClick?: (value?: string) => void
   onEndIconClick?: (value?: string) => void
   onChange?: (value: string) => void
   onKeyDown?: React.KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement>
   onClear?: () => void,
   maxDecimals?: number,
   multiline?: number,
   labelClassName?: string,
}

const InputField: React.FC<InputFieldProps> = (
   { acceptChars, labelClassName, ariaLabel, autoFocus, clearable, description, disabled = false, error, successIcon, maxChars, title, placeholder, endIcon, startIcon, startIconLabel, endIconLabel, theme, type, name, autoComplete, ref, value, onClear, onStartIconClick, onEndIconClick, onChange, onKeyDown, maxDecimals, multiline }
) => {
   const fieldId = useId()
   const errorId = `${fieldId}-error`
   const descriptionId = `${fieldId}-description`
   const leftSpace = `${onStartIconClick !== undefined ? 'pl-2.5' : 'pl-3.75'}`
   const rightSpace = `pr-2`
   const border = error ? 'border-twRed' : 'border-borderStrong'
   const disabledBg = disabled === true ? 'bg-lightGray' : ''

   const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      let val = e.target.value;

      if (type === 'number' && maxDecimals) {
         if (val.includes('.')) {
            const [intPart, decPart] = val.split('.');
            val = intPart + '.' + decPart.slice(0, maxDecimals);
         }
      }

      onChange?.(val);
   };

   const handleBeforeInput = acceptChars
      ? (e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement> & { data?: string }) => {
         if (!e.data) return;
         if (!acceptChars.test(e.data)) e.preventDefault();
      }
      : undefined;

   const handlePaste = acceptChars
      ? (e: React.ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
         const pasted = e.clipboardData.getData("text");
         if (!acceptChars.test(pasted)) e.preventDefault();
      }
      : undefined;

   const ariaDescribedBy = [error ? errorId : null, description ? descriptionId : null].filter(Boolean).join(' ') || undefined;

   const sharedProps = {
      id: fieldId,
      className: twMerge(
         `bg-transparent border-none outline-none h-full w-full text-sm placeholder-twGray`,
         theme?.input || ""
      ),
      disabled,
      maxLength: maxChars,
      placeholder,
      value: value || "",
      onChange: handleChange,
      onBeforeInput: handleBeforeInput,
      onPaste: handlePaste,
      name,
      autoComplete,
      type,
      autoFocus,
      onKeyDown,
      "aria-invalid": error !== undefined ? true : undefined,
      ...(ariaDescribedBy && { "aria-describedby": ariaDescribedBy }),
      ...(ariaLabel && { "aria-label": ariaLabel })
   };

   return (
      <div className={theme?.container}>
         {/* Title / Label */}
         {title !== undefined && (
            <label htmlFor={fieldId} className={twMerge("m-0 font-semibold text-sm mb-2.5 block", labelClassName)}>
               {title}
            </label>
         )}
         {/* Main input */}
         <div
            className={twMerge(`flex bg-white items-center border rounded-md pr-3.75 gap-x-2 h-11.5 focus-within:ring-2 focus-within:ring-focusRing`, leftSpace, rightSpace, border, disabledBg, theme?.border)}
         >
            {startIcon !== undefined && (
               <button aria-label={startIconLabel} className={twMerge("hover:bg-border rounded-md w-8.5 min-w-8.5 max-w-8.5 h-8.5 flex justify-center items-center", theme?.startIcon)}
                  onClick={(e) => {
                     e.preventDefault()
                     e.stopPropagation()

                     onStartIconClick?.(value)
                  }}
               >
                  {startIcon}
               </button>
            )}
            {multiline && multiline > 1 ? <textarea {...sharedProps} rows={multiline} /> : <input ref={ref} {...sharedProps} />}
            {error !== undefined && <TbExclamationCircleFilled size={24} color="#E02424" />}
            {successIcon !== undefined && successIcon}
            {clearable && <button
               type="button"
               aria-label={ICON_BUTTON_LABELS.CLEAR}
               className={`p-[3px] rounded-full bg-twGray mr-1 ${value?.trim() !== '' ? 'visible' : 'hidden'}`}
               onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()

                  onClear?.()
               }}
            >
               <HiX size={10} color='white' />
            </button>}
            {endIcon !== undefined && (
               <button
                  aria-label={endIconLabel}
                  className={twMerge("hover:enabled:bg-border rounded-md w-8.5 min-w-8.5 h-8.5 flex justify-center items-center", theme?.endIcon)}
                  disabled={onEndIconClick === undefined}
                  onClick={(e) => {
                     e.preventDefault()
                     e.stopPropagation()

                     onEndIconClick?.(value)
                  }}>
                  {endIcon}
               </button>
            )}
         </div>
         {/* Error text */}
         {error !== undefined && <p id={errorId} role="alert" className="m-0 text-twRed mt-2.5 text-xs">{error}</p>}
         {/* Description / Help text */}
         {description !== undefined && <div id={descriptionId} className="m-0 mt-2.5 text-twGray text-sm leading-4.5">{description}</div>}
      </div>
   )
}

export default InputField

